import { FC, MouseEvent, forwardRef, PropsWithoutRef, Ref, KeyboardEvent } from 'react';
import styled, { css } from 'styled-components';
import { mix, transparentize } from 'polished';

import { ForwardProps } from '../../types';
import { tryCatch } from '../../utils/utils';
import BareButton, { StyledBareButton } from '../Button/BareButton';
import Icon, { registerIcon, StyledIcon } from '../Icon';
import * as timesIcon from '../Icon/icons/times.icon';
import { useConsolidatedRef } from '../../hooks';
import { FormControlProps } from '../FormControl';

registerIcon(timesIcon);

export interface SelectableProps {
  /** An id that is used by the onSelect and onRemove functions if they are provided. */
  id: string;
  /** Takes an ID that represents the Badge. This function is called when the Badge is clicked. */
  onSelect?: (id: string) => void;
  /** Takes an ID that represents the Badge. This function is called when the Badge is closed by clicking the X. */
  onRemove?: (id: string) => void;
  /** The content of the Badge. */
  children: string;
  /** Makes the input non editable and non clickable. The browser defaults to false. */
  readOnly?: FormControlProps['readOnly'];
  /** Event handler called when the Badge is clicked. */
  onClick?: (event: MouseEvent) => void;
  /** Event handler fired on every keyup event. */
  onKeyUp?: (event: KeyboardEvent) => void;
}

export const StyledSelectable = styled.div(props => {
  const { foreground, background } = props.theme.components.badges.selectable.base;
  const { dark } = props.theme.base.palette;
  const borderRadius = props.theme.base['border-radius'];
  const { spacing } = props.theme.base;

  const displayBackground = tryCatch(() => mix(0.15, background, '#ffffff'));
  const hoverBorder = tryCatch(() => mix(0.3, background, '#ffffff'));
  const boxShadowColor = tryCatch(() => transparentize(0.45, dark));

  const height = 1.125;
  return css`
    background: ${displayBackground};
    border: 0 solid transparent;
    border-radius: calc(${height} * ${borderRadius});
    color: ${foreground};
    cursor: default;
    display: inline-flex;
    align-items: center;
    font-size: 0.75rem;
    height: ${height}rem;
    padding: 0 ${spacing};
    min-width: max-content;
    line-height: 1;

    &:hover {
      color: ${props.theme.base.palette.interactive};
      box-shadow: inset 0 0 0 0.0625rem ${hoverBorder};

      ${StyledBareButton} {
        color: ${props.theme.base.palette.interactive};
      }
    }

    &:active {
      box-shadow: inset 0 0 0 0.0625rem ${background};
    }

    &:focus {
      color: ${props.theme.base.palette.interactive};
      box-shadow: 0 0 0.5rem -0.125rem ${boxShadowColor}, inset 0 0 0 0.125rem ${background};
      outline: none;

      ${StyledBareButton} {
        color: ${props.theme.base.palette.interactive};
      }
    }

    ${StyledIcon} {
      margin-inline-start: ${spacing};
      height: 1em;
      width: 1em;
    }
  `;
});

const Selectable: FC<SelectableProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<SelectableProps>, ref: Ref<HTMLButtonElement>) => {
    const consolidatedRef = useConsolidatedRef(ref);
    const { id, onSelect, onRemove, children, onClick, onKeyUp, ...restProps } = props;

    const handleClick = (e: MouseEvent<HTMLDivElement>) => {
      onClick?.(e);
      onSelect?.(id);
    };

    const handleCloseClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onRemove?.(id);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      onKeyUp?.(e);
      if (e.key === 'Enter') {
        onSelect?.(id);
      }

      if (e.key === 'Backspace' || e.key === 'Delete') {
        onRemove?.(id);
      }
    };

    return (
      <BareButton
        role='button'
        tabIndex='0'
        as={StyledSelectable}
        ref={consolidatedRef}
        onClick={handleClick}
        onKeyUp={handleKeyUp}
        {...restProps}
      >
        {children}
        <BareButton tabIndex='-1' onClick={handleCloseClick}>
          <Icon name='times' />
        </BareButton>
      </BareButton>
    );
  }
);

export default Selectable;
